import axios from 'axios';

interface ApiErrorEnvelope {
  code?: string;
  details?: Record<string, unknown>;
}

export const getTassoQuintoErrorCode = (error: unknown): string | null => {
  if (!axios.isAxiosError<ApiErrorEnvelope>(error)) {
    return null;
  }

  return error.response?.data?.code ?? null;
};

export const getTassoQuintoErrorStatus = (error: unknown): number | null => {
  if (!axios.isAxiosError(error)) {
    return null;
  }

  return error.response?.status ?? null;
};

/**
 * The envelope's `details`, whose shape belongs to the code that named it: this
 * stays a plain bag so a reader can pick the one key its own code documents.
 */
export const getTassoQuintoErrorDetails = (
  error: unknown
): Record<string, unknown> => {
  if (!axios.isAxiosError<ApiErrorEnvelope>(error)) {
    return {};
  }

  const details = error.response?.data?.details;
  return details && typeof details === 'object' ? details : {};
};

/**
 * `invalid_request` reports the offending field names only — never the values
 * the browser sent, Nota text included — so this stays a list of names.
 */
export const getTassoQuintoErrorFields = (error: unknown): string[] => {
  const fields = getTassoQuintoErrorDetails(error).fields;
  return Array.isArray(fields)
    ? fields.filter((field): field is string => typeof field === 'string')
    : [];
};

export const isHubspotOwnerMissingError = (error: unknown) =>
  getTassoQuintoErrorCode(error) === 'hubspot_owner_missing';

/**
 * What a failed read means for the page that asked for it. One vocabulary for
 * every managed lead page: the lists, the detail, the Notes and the
 * administrative queue all owe the operator the same seven answers, and they
 * must not disagree about which one a given response deserves.
 */
export type TassoQuintoErrorKind =
  | 'session_expired'
  | 'auth_unavailable'
  | 'unauthorized'
  | 'not_found'
  | 'owner_missing'
  | 'retryable';

const KIND_BY_CODE: Record<string, TassoQuintoErrorKind> = {
  invalid_token: 'session_expired',
  invalid_backoffice_token: 'session_expired',
  authentication_unavailable: 'auth_unavailable',
  insufficient_scope: 'unauthorized',
  deal_not_found: 'not_found',
  hubspot_owner_missing: 'owner_missing',
};

/**
 * The status is the fallback, never the rule. A response that names a stable
 * code is classified by it, as the contract requires; a response that names
 * none — a gateway that answered for the API, a code released after this
 * frontend — is still better served by its status than by a Riprova the
 * operator can only press forever.
 */
const KIND_BY_STATUS: Record<number, TassoQuintoErrorKind> = {
  401: 'session_expired',
  403: 'unauthorized',
  404: 'not_found',
};

export const classifyTassoQuintoError = (
  error: unknown
): TassoQuintoErrorKind => {
  const code = getTassoQuintoErrorCode(error);
  if (code && KIND_BY_CODE[code]) return KIND_BY_CODE[code];

  const status = getTassoQuintoErrorStatus(error);
  return (status !== null && KIND_BY_STATUS[status]) || 'retryable';
};
